import { useCallback, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { ClassicConfig } from './classic-config'
import {
  BITLY_QR_CODE_ENABLED,
  BITLY_QR_CODE_CONFIG,
  BITLY_QR_CODE_USE_LONG_URL,
  BITLY_QR_CODE_DONT_REGENERATE,
} from './field-configs'
import { CheckboxField } from '../../components'
import { t } from '@lingui/core/macro'

const MarginFormControl = styled(FormControlLabel)`
  align-content: left;
`

const useStyles = makeStyles(() => ({
  subHeading: {
    margin: '20px 0 5px 0',
  },
}))

interface BitlyQRCodeProps {
  assignment: any
  onMetadataChange: (value: any) => void
}

export const BitlyQRCode = ({ assignment, onMetadataChange }: BitlyQRCodeProps) => {
  const classes = useStyles()

  const isEnabled = (key, assignment) =>
    assignment?.metadata?.[key] !== '0' && Boolean(assignment?.metadata?.[key])

  const isQrCodeEnabled = useMemo(() => isEnabled(BITLY_QR_CODE_ENABLED, assignment), [assignment])

  const jsonData = useMemo(() => {
    try {
      return JSON.parse(assignment?.metadata?.[BITLY_QR_CODE_CONFIG])
    } catch {
      return {}
    }
  }, [assignment?.metadata?.[BITLY_QR_CODE_CONFIG]])

  const isTransparent = useMemo(
    () => /^#[0-9A-F]{6}00/i.test(jsonData?.background_color),
    [jsonData?.background_color],
  )

  const onChangeTransparency = useCallback(
    checked => {
      const config = { ...jsonData }

      if (checked) {
        config.background_color = '#ffffff00'
      } else {
        delete config.background_color
      }

      onMetadataChange({ [BITLY_QR_CODE_CONFIG]: JSON.stringify(config) })
    },
    [onMetadataChange, jsonData],
  )

  useEffect(() => {
    if (isQrCodeEnabled && !assignment?.metadata?.qr_code_config) {
      onMetadataChange({ [BITLY_QR_CODE_CONFIG]: '{}' })
    }
  }, [isQrCodeEnabled, assignment?.metadata?.qr_code_config, onMetadataChange])

  return (
    <>
      <CheckboxField
        label={t`Enable QR code`}
        integrationAssignment={assignment}
        checked={isQrCodeEnabled}
        metadataKey={BITLY_QR_CODE_ENABLED}
      />
      {isQrCodeEnabled && (
        <>
          <Typography variant="h4" className={classes.subHeading}>
            {t`QR code customization`}
          </Typography>
          <MarginFormControl
            control={
              <Checkbox
                checked={isTransparent}
                onChange={e => {
                  onChangeTransparency(e.target.checked)
                }}
              />
            }
            label={t`Transparent`}
          />
          <Box>
            <CheckboxField
              label={t`Use link instead of the shortened URL`}
              integrationAssignment={assignment}
              checked={isEnabled(BITLY_QR_CODE_USE_LONG_URL, assignment)}
              metadataKey={BITLY_QR_CODE_USE_LONG_URL}
            />
            <CheckboxField
              label={t`Don't regenerate QR code if already exists`}
              integrationAssignment={assignment}
              checked={isEnabled(BITLY_QR_CODE_DONT_REGENERATE, assignment)}
              metadataKey={BITLY_QR_CODE_DONT_REGENERATE}
            />
          </Box>
          <ClassicConfig
            metadataKey={BITLY_QR_CODE_CONFIG}
            handleChange={value => {
              onMetadataChange({ [BITLY_QR_CODE_CONFIG]: value })
            }}
            value={assignment?.metadata?.qr_code_config}
            jsonData={jsonData}
          />
        </>
      )}
    </>
  )
}
